/**
 * Pure layout helpers for the Money → Revenue vs usage quadrant (screens/money/RevenueUsage.tsx Quad): the y scale
 * with outliers drawn off the scale, and collision-free point labels (t-95161fd5).
 */
const ceilEven = (v: number) => Math.ceil(v / 2) * 2;

/**
 * The y scale: linear 0–max with ticks every max/5 (max at least 10), the prototype's clean axis. When one or a few
 * clients are revenue outliers the axis covers the rest of the clients, the cluster, and each outlier is drawn OFF THE
 * SCALE: pinned in a thin strip above the top gridline with an up-arrow and its value in the label. One ₹47 L client
 * no longer squashes everyone else into the floor, and the axis stays a plain 0–10 (t-95161fd5). An outlier is above
 * 10 L and more than twice the next client's revenue; at most a third of the clients can be outliers (the biggest
 * gap wins).
 */
export function revenueScale(values: number[], top: number, bottom: number) {
  const v = [...values].filter((x) => Number.isFinite(x)).sort((a, b) => b - a);
  const maxOut = Math.max(1, Math.floor(v.length / 3));
  let cut = -1;
  let best = 2;
  for (let k = 0; k < Math.min(maxOut, v.length - 1); k++) {
    const ratio = v[k]! / Math.max(v[k + 1]!, 5);
    if (v[k]! > 10 && ratio > best) {
      best = ratio;
      cut = k;
    }
  }
  // The strip sits BELOW the quadrant-title band (titles: top+3..top+17, see quadrantTitles): the caret starts at
  // stripY-14 = top+18 and the marker ends at stripY+9 = top+41, so neither covers a title at any usage, and the top
  // gridline (top+STRIP) stays 7 px under the marker (t-03f2bc48).
  const STRIP = 48;
  const max = Math.max(10, ceilEven(cut < 0 ? Math.max(0, ...v) : v[cut + 1]!));
  const plotTop = cut < 0 ? top : top + STRIP;
  const stripY = cut < 0 ? null : top + 32;
  const isOff = (m: number) => cut >= 0 && m > max;
  const py = (m: number) => (isOff(m) ? stripY! : bottom - (m / max) * (bottom - plotTop));
  return { py, isOff, stripY, max, ticks: Array.from({ length: 6 }, (_, i) => (max / 5) * i) };
}

export type Box = { x: number; y: number; w: number; h: number };

/**
 * The two quadrant titles' boxes ('Revenue at risk' top-left, starting at usage 2%; 'Healthy core' top-right, end-
 * anchored 4 px inside the plot), baseline at top+14 with the 'y' descender, so top+3..top+17. Quad draws them from
 * these and the labels and the off-scale strip keep clear of them.
 */
export function quadrantTitles(px: (u: number) => number, top: number, right: number) {
  return {
    risk: { x: px(2), y: top + 3, w: 96, h: 14, baseline: top + 14 },
    healthy: { x: right - 4 - 76, y: top + 3, w: 76, h: 14, baseline: top + 14, endX: right - 4 },
  };
}

/** What an off-scale point draws: the marker (r 9) and the up caret above it (stripY-14..stripY-10). */
export const offScaleMarkBox = (x: number, y: number): Box => ({ x: x - 10, y: y - 14, w: 20, h: 24 });
const overlap = (a: Box, b: Box) => Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) * Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));

/**
 * Greedy label placement: each point tries right, left, above, below and the diagonals, and keeps the first spot that
 * hits no marker, no fixed text (the quadrant titles), no label already placed and stays inside the plot; if every spot collides it takes the one with the
 * least overlap. Points are placed top to bottom, left to right, so the result is stable.
 */
export function placeLabels(pts: { id: string; x: number; y: number; text: string }[], bounds: Box, obstacles: Box[] = []) {
  const CH = 6.4, LH = 13, R = 9;
  const markers: Box[] = [...pts.map((p) => ({ x: p.x - R, y: p.y - R, w: 2 * R, h: 2 * R })), ...obstacles];
  const placed: Box[] = [];
  const out = new Map<string, { x: number; y: number; anchor: 'start' | 'end' | 'middle' }>();
  const order = [...pts].sort((a, b) => a.y - b.y || a.x - b.x);
  for (const p of order) {
    const w = p.text.length * CH + 2;
    const cands: { x: number; y: number; anchor: 'start' | 'end' | 'middle'; box: Box }[] = [
      { x: p.x + 12, y: p.y + 4, anchor: 'start', box: { x: p.x + 12, y: p.y - 8, w, h: LH } },
      { x: p.x - 12, y: p.y + 4, anchor: 'end', box: { x: p.x - 12 - w, y: p.y - 8, w, h: LH } },
      { x: p.x, y: p.y - 13, anchor: 'middle', box: { x: p.x - w / 2, y: p.y - 24, w, h: LH } },
      { x: p.x, y: p.y + 22, anchor: 'middle', box: { x: p.x - w / 2, y: p.y + 11, w, h: LH } },
      { x: p.x + 10, y: p.y - 9, anchor: 'start', box: { x: p.x + 10, y: p.y - 20, w, h: LH } },
      { x: p.x + 10, y: p.y + 18, anchor: 'start', box: { x: p.x + 10, y: p.y + 7, w, h: LH } },
      { x: p.x - 10, y: p.y - 9, anchor: 'end', box: { x: p.x - 10 - w, y: p.y - 20, w, h: LH } },
      { x: p.x - 10, y: p.y + 18, anchor: 'end', box: { x: p.x - 10 - w, y: p.y + 7, w, h: LH } },
    ];
    let pick = cands[0]!;
    let bestScore = Infinity;
    for (const c of cands) {
      const b = c.box;
      const outside = b.x < bounds.x || b.y < bounds.y || b.x + b.w > bounds.x + bounds.w || b.y + b.h > bounds.y + bounds.h ? 1e6 : 0;
      const hits = placed.reduce((s, o) => s + overlap(b, o), 0) + markers.reduce((s, o) => s + overlap(b, o), 0);
      const score = outside + hits;
      if (score < bestScore) {
        bestScore = score;
        pick = c;
        if (score === 0) break;
      }
    }
    placed.push(pick.box);
    out.set(p.id, { x: pick.x, y: pick.y, anchor: pick.anchor });
  }
  return out;
}
